/**
 * KAN-880: строка фильтров лидов, общая для доски и таблицы CRM: поиск (имя,
 * телефон, компания, ИНН, Telegram) с дебаунсом и выбор менеджера. Состояние в
 * адресе (useCrmLeadFilters), компонент только рисует и пересылает изменения.
 * Менеджер: "Все менеджеры" (без фильтра), "Мои" (только пользователь-менеджер),
 * "Без менеджера", затем активные менеджеры.
 * KAN-883: кнопка-переключатель "На меня" (manager=me) рядом с выбором менеджера,
 * только у пользователя со своей записью менеджера (manager_id).
 * KAN-909: флажок "Без движения больше 7 дней" убран и с доски (его заменила кнопка
 * "Нет движения"); "Мои" и "На меня" у входа со своей записью менеджера (hasOwnManager);
 * менеджер по умолчанию - свой (useCrmLeadFilters).
 * KAN-883: в виде "Лиды по дням" только выбор менеджера.
 * KAN-906: compact - поля h-8, поиск уже, корень display: contents (поля встают
 * в строку фильтров таблицы CRM). showManager - без выбора менеджера и "На меня"
 * (на телефоне поиск в строке, менеджер в панели "Фильтры").
 */
import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDebounce } from '@/hooks/useDebounce';
import type { CrmLeadFiltersState } from '@/hooks/useCrmLeadFilters';
import { useAssignableManagers } from '@/hooks/useManagers';
import { MANAGER_FILTER_ME, MANAGER_FILTER_NONE } from '@/lib/crmLeadFilters';
import { cn } from '@/lib/utils';

/** Задержка поиска: промежуточные строки набора в запрос не уходят. */
export const CRM_SEARCH_DEBOUNCE_MS = 300;
/** Значение пункта "Все менеджеры" (Radix Select не принимает пустую строку). */
const ALL_MANAGERS = '__all__';

interface CrmLeadFiltersProps {
  state: CrmLeadFiltersState;
  /** Поле поиска (нет в "Лиды по дням"). */
  showSearch?: boolean;
  /** data-testid кнопки "На меня" (доска и таблица). */
  meTestId?: string;
  /** KAN-906: компактная строка (h-8, поиск ~200px, без своей обёртки-строки). */
  compact?: boolean;
  /** KAN-906: выбор менеджера и "На меня". */
  showManager?: boolean;
}

export function CrmLeadFilters({
  state,
  showSearch = true,
  meTestId = 'crm-manager-me',
  compact = false,
  showManager = true,
}: CrmLeadFiltersProps): JSX.Element {
  const { filters, hasOwnManager, setSearch, setManager } = state;
  const [text, setText] = useState(filters.search);
  const debounced = useDebounce(text, CRM_SEARCH_DEBOUNCE_MS);
  const { data: managers } = useAssignableManagers();

  useEffect(() => {
    if (debounced !== filters.search) setSearch(debounced);
    // filters.search не в зависимостях: внешняя смена адреса не должна перезаписывать набор.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, setSearch]);

  useEffect(() => {
    // Адрес сменился снаружи (назад, ссылка): поле показывает новое значение.
    setText((current) => (current.trim() === filters.search.trim() ? current : filters.search));
  }, [filters.search]);

  const height = compact ? 'h-8' : 'h-9';
  return (
    <div
      data-testid="crm-lead-filters"
      className={compact ? 'contents' : 'flex min-w-0 flex-wrap items-center gap-2'}
    >
      {showSearch && (
        <div className={cn('relative w-full min-w-0', compact ? 'sm:w-52' : 'sm:w-64')}>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <Input
            type="search"
            aria-label="Поиск"
            placeholder="Имя, телефон, компания, ИНН, @ник"
            value={text}
            onChange={(event) => setText(event.target.value)}
            className={cn(height, 'pl-8', compact && 'text-sm')}
          />
        </div>
      )}
      {showManager && (
        <Select
          value={filters.manager ?? ALL_MANAGERS}
          onValueChange={(value) => setManager(value === ALL_MANAGERS ? null : value)}
        >
          <SelectTrigger
            data-testid="crm-manager-filter"
            aria-label="Менеджер"
            className={cn(height, 'w-auto', compact ? 'min-w-[9rem] text-sm' : 'min-w-[11rem]')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_MANAGERS}>Все менеджеры</SelectItem>
            {hasOwnManager && <SelectItem value={MANAGER_FILTER_ME}>Мои</SelectItem>}
            <SelectItem value={MANAGER_FILTER_NONE}>Без менеджера</SelectItem>
            {managers.map((manager) => (
              <SelectItem key={manager.id} value={manager.id}>
                {manager.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {showManager && hasOwnManager && (
        <button
          type="button"
          data-testid={meTestId}
          aria-pressed={filters.manager === MANAGER_FILTER_ME}
          onClick={() => setManager(filters.manager === MANAGER_FILTER_ME ? null : MANAGER_FILTER_ME)}
          className={cn(
            height,
            'whitespace-nowrap rounded-lg border px-3 text-sm font-medium transition-colors',
            filters.manager === MANAGER_FILTER_ME
              ? 'border-blue-600 bg-blue-50 text-blue-800'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          )}
        >
          На меня
        </button>
      )}
    </div>
  );
}
